import { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import DocumentTraceView from './DocumentTraceView';

interface TraceJobNode {
  id: string;
  name: string;
  status: string;
  parentJobId: string | null;
  createdAt: number;
  data: any;
}

interface TraceViewProps {
  traceId: string;
}

// Document processing job types for identification
const DOCUMENT_JOBS = [
  'DocumentUploadIngestJob',
  'TextExtractionJob', 
  'MetadataExtractionJob',
  'DocumentVectorizationJob',
  'DocumentIngestJob'
];

const isDocumentJob = (jobName?: string) => {
  return jobName && DOCUMENT_JOBS.some(docJob => jobName.includes(docJob));
};

export default function TraceView({ traceId }: TraceViewProps) {
  const { keycloak } = useKeycloak();
  const [jobs, setJobs] = useState<TraceJobNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTrace = async () => {
      if (!keycloak?.token) return;

      try {
        const res = await fetch('/api/v1/paralex-mcp/tools/get_job_trace', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ jobId: traceId })
        });

        if (!res.ok) throw new Error(`Server responded with ${res.status}`);
        const response = await res.json();
        const data = response.result || response;
        setJobs(data.jobs || []);
        setError(null);
      } catch (err: any) {
        console.error('Failed to load job trace:', err);
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadTrace();
  }, [traceId, keycloak.token]);

  if (loading) {
    return <p className="text-gray-600 dark:text-gray-300">🔄 Loading trace...</p>;
  }

  if (error) {
    return <p className="text-red-500 dark:text-red-400">⚠️ Error loading trace: {error}</p>;
  }

  if (!jobs.length) {
    return (
      <div className="mt-6">
        <p className="text-gray-500 dark:text-gray-400 italic mb-6">
          No jobs found for trace <strong>{traceId}</strong>
        </p>
        
        <div className="p-4 bg-lex-accent-soft dark:bg-lex-accent-soft rounded-lg border border-lex-accent">
          <p className="text-lex-accent text-sm">
            💡 <strong>Need help analyzing this trace?</strong> Use the ParaLex System Chat from the main System Monitor page.
          </p>
        </div>
      </div>
    );
  }

  // Check if this is a document processing trace
  const documentJobs = jobs.filter(job => isDocumentJob(job.name));
  const isDocumentTrace = documentJobs.length > 0;

  // Use DocumentTraceView for document processing traces
  if (isDocumentTrace) {
    return <DocumentTraceView traceId={traceId} />;
  }

  // Standard trace view for non-document traces
  const buildTree = (parentId: string | null): React.ReactElement[] => {
    const children = jobs.filter(job => job.parentJobId === parentId);
    return children.map(job => {
      const childTree = buildTree(job.id);
      // Get agent role and trace info from job data if available
      const agentRole = job.data?.agent_role || job.data?.agentRole;
      const jobDesc = job.data?.job_description || job.data?.jobDescription;
      
      return (
        <li key={job.id} className="ml-4 mt-2 border-l-2 border-lex-accent pl-4">
          <div className="text-sm text-gray-900 dark:text-gray-100">
            <span className="font-semibold">{job.name}</span>{' '}
            {agentRole && (
              <span className="px-2 py-1 ml-2 rounded-full text-xs bg-lex-accent-soft text-lex-accent">
                {agentRole}
              </span>
            )}
            <span className="text-xs text-gray-500 ml-2">({job.status})</span>
          </div>
          {jobDesc && (
            <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
              {jobDesc}
            </div>
          )}
          <div className="text-xs text-gray-500">
            {new Date(job.createdAt).toLocaleString()}
          </div>
          {childTree.length > 0 && <ul>{childTree}</ul>}
        </li>
      );
    });
  };

  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Standard Trace View</h2>
      <ul className="mt-2">{buildTree(null)}</ul>
      
      <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-lex-accent-soft dark:from-purple-900/20 dark:to-lex-accent-soft rounded-lg border border-purple-200 dark:border-purple-700">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🤖</span>
          <div>
            <h4 className="font-semibold text-purple-800 dark:text-purple-200">ParaLex System Agent</h4>
            <p className="text-purple-700 dark:text-purple-300 text-sm">
              For AI-powered analysis of this trace, use the ParaLex chat from the main System Monitor page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
